import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { LEGAL_SUITE_VERSION } from '@/constants/legalSuite';

const COOKIE_CONSENT_KEY = 'foco_cookie_consent';

interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
  consentedAt: string;
}

function getStoredConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function CookieConsentBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(true);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Small delay so it doesn't flash immediately
      const t = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(t);
    }
  }, []);

  const saveConsent = async (consent: CookieConsent) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);

    // If user is logged in, persist to database
    if (user) {
      try {
        // Save cookie preferences
        await supabase.from('cookie_preferences').upsert({
          user_id: user.id,
          analytics_cookies: consent.analytics,
          functional_cookies: consent.functional,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

        // Save consent record as evidence
        await supabase.from('consent_records').insert({
          user_id: user.id,
          consent_type: 'cookies',
          consent_version: LEGAL_SUITE_VERSION,
          accepted: true,
          user_agent: navigator.userAgent,
        });
      } catch (e) {
        console.error('Error saving consent:', e);
      }
    }
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      functional: true,
      consentedAt: new Date().toISOString(),
    });
  };

  const handleAcceptSelected = () => {
    saveConsent({
      essential: true,
      analytics,
      functional,
      consentedAt: new Date().toISOString(),
    });
  };

  const handleRejectOptional = () => {
    saveConsent({
      essential: true,
      analytics: false,
      functional: false,
      consentedAt: new Date().toISOString(),
    });
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4">
      <div className="max-w-2xl mx-auto bg-card border rounded-xl shadow-lg p-4 sm:p-6">
        {!showPreferences ? (
          /* Main banner */
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Uso de cookies y almacenamiento local</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Utilizamos cookies esenciales para que la plataforma funcione correctamente y tecnologías opcionales
                  para mejorar tu experiencia. Puedes aceptar todas, personalizar tus preferencias o rechazar las opcionales.{' '}
                  <Link to="/privacidad" className="text-primary underline">Centro de privacidad</Link>
                  {' · '}
                  <Link to="/privacy-policy" className="text-primary underline">Privacidad</Link>
                  {' · '}
                  <Link to="/cookies-policy" className="text-primary underline">Cookies</Link>
                  {' '}(políticas versión {LEGAL_SUITE_VERSION}).
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={handleAcceptAll}>
                Aceptar todas
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowPreferences(true)}>
                <Settings2 className="h-4 w-4 mr-1" />
                Personalizar
              </Button>
              <Button size="sm" variant="ghost" onClick={handleRejectOptional}>
                Solo esenciales
              </Button>
            </div>
          </div>
        ) : (
          /* Preferences panel */
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Preferencias de cookies</h3>
            </div>

            {/* Essential - always on */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="text-sm font-medium text-foreground">Esenciales</p>
                <p className="text-xs text-muted-foreground">Autenticación, seguridad y funcionamiento básico.</p>
              </div>
              <Switch checked disabled />
            </div>

            {/* Functional */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="text-sm font-medium text-foreground">Funcionales</p>
                <p className="text-xs text-muted-foreground">Tema visual, moneda predeterminada y preferencias de gráficos.</p>
              </div>
              <Switch checked={functional} onCheckedChange={setFunctional} />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-foreground">Analíticas</p>
                <p className="text-xs text-muted-foreground">Nos ayudan a mejorar la plataforma (actualmente no activas).</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button size="sm" onClick={handleAcceptSelected}>
                Guardar preferencias
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPreferences(false)}>
                Volver
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Utility to read current cookie consent from localStorage */
export function getCookieConsent(): CookieConsent | null {
  return getStoredConsent();
}
